{********************************************************************}
{                                                                    }
{ written by TMS Software                                            }
{            copyright (c) 2024                                      }
{            Email : info@tmssoftware.com                            }
{            Web : https://www.tmssoftware.com                       }
{                                                                    }
{ The source code is given as is. The author is not responsible      }
{ for any possible damage done due to the use of this code.          }
{ The complete source code remains property of the author and may    }
{ not be distributed, published, given or sold in any form as such.  }
{ No parts of the source code can be included in any other component }
{ or application without written authorization of the author.        }
{********************************************************************}

unit WEBLib.TMSFNCDataGridCore;

interface

{$I WEBLib.TMSFNCDefines.inc}

uses
  Classes, Types, Generics.Collections, WEBLib.TMSFNCTypes, WEBLib.TMSFNCCustomControl, WEBLib.TMSFNCPersistence
  {$IFDEF WEBLIB}
  ,SysUtils
  {$ENDIF}
  ;

type
  TTMSFNCDataGridRowType = (grtDefault, grtSummary, grtNode, grtFilter);

  TTMSFNCDataGridRowNodeState = (grnsCollapsed, grnsExpanded);

  TTMSFNCDataGridIntegerDictionary = class(TDictionary<Integer, Integer>);
  TTMSFNCDataGridBooleanDictionary = class(TDictionary<Integer, Boolean>);
  TTMSFNCDataGridDoubleDictionary = class(TDictionary<Integer, Double>);

  TTMSFNCDataGridLogMessageType = (glmInfo, glmWarning, glmError);
  TTMSFNCDataGridLogMessageEvent = procedure(Sender: TObject; AMessage: string; AMessageType: TTMSFNCDataGridLogMessageType) of object;

  TTMSFNCDataGridCellCoord = record
  private
    FColumn, FRow: Integer;
    function GetRow: Integer;
    function GetColumn: Integer;
    procedure SetRow(const Value: Integer);
    procedure SetColumn(const Value: Integer);
    function GetToString: string;
  public
    function IsEqualTo(ACell: TTMSFNCDataGridCellCoord): Boolean;
    property Column: Integer read GetColumn write SetColumn;
    property Row: Integer read GetRow write SetRow;
    property ToString: string read GetToString;
    class function FromString(AValue: string): TTMSFNCDataGridCellCoord; static;
  end;

  TTMSFNCDataGridCellCoordRange = record
    StartCell, StopCell: TTMSFNCDataGridCellCoord;
  end;

  TTMSFNCDataGridSortDirection = (gsdNone, gsdAscending, gsdDescending);

  TTMSFNCDataGridSortFormat = (gsfAutomatic, gsfAlphabetic, gsfNumeric, gsfDateTime, gsfCustom);

  TTMSFNCDataGridExportState = (gesExportStart, gesExportNewRow, gesExportDone, gesExportSelectedRow, gesExportFail, gesExportNextRow, gesExportFindRow);
  TTMSFNCDataGridImportState = (gisImportStart, gisImportNewRow, gisImportDone, gisImportSelectedRow);

  TTMSFNCDataGridStretchingMode = (gstmIndex, gstmAll);

  TTMSFNCDataGridStretchingRange = (gstrNormal, gstrAll);

  TTMSFNCDataGridCore = class(TInterfacedPersistent, ITMSFNCPersistence)
  private
    class var FBlockPersistenceInterface: Boolean;
  private
    FExportRectSet: Boolean;
    FLocalRect, FExportRect, FSaveLocalRect: TRectF;
    FColumnCount: Integer;
    FColumnOrderDictionary, FRowOrderDictionary: TTMSFNCDataGridIntegerDictionary;
    FColumnReverseOrderDictionary, FRowReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary;
    FColumnWidthDictionary, FRowHeightDictionary, FColumnPositionDictionary, FRowPositionDictionary: TTMSFNCDataGridDoubleDictionary;
    FDefaultColumnWidth: Double;
    FDefaultRowHeight: Double;
    FFilteredRowDictionary: TTMSFNCDataGridBooleanDictionary;
    FFixedBottomRowCount: Integer;
    FFixedRightColumnCount: Integer;
    FFixedRowCount, FFixedColumnCount: Integer;
    FFreezeRowCount, FFreezeColumnCount: Integer;
    FHiddenColumnDictionary: TTMSFNCDataGridBooleanDictionary;
    FHiddenRowDictionary: TTMSFNCDataGridBooleanDictionary;
    FMergedColumnDictionary: TTMSFNCDataGridBooleanDictionary;
    FMergedRowDictionary: TTMSFNCDataGridBooleanDictionary;
    FOnLogMessage: TTMSFNCDataGridLogMessageEvent;
    FRowCount: Integer;
    FStartColumn, FStopColumn, FStartRow, FStopRow: Integer;
    FTotalColumnWidth: Double;
    FTotalRowHeight: Double;
    FUpdateCount: Integer;
    FIsExporting: Boolean;
    FTotalFixedColumnWidth: Double;
    FTotalFixedBottomRowHeight: Double;
    FTotalFixedRightColumnWidth: Double;
    FTotalFixedRowHeight: Double;
    FAppearancePersisting: Boolean;
    FOnCanSaveProperty: TTMSFNCCustomControlCanSavePropertyEvent;
    FOnCanLoadProperty: TTMSFNCCustomControlCanLoadPropertyEvent;
    FName: string;
    function GetColumnOrderDictionary: TTMSFNCDataGridIntegerDictionary;
    function GetColumnOrder(AColumn: Integer): Integer;
    function GetColumnPositionDictionary: TTMSFNCDataGridDoubleDictionary;
    function GetColumnPosition(AColumn: Integer): Double;
    function GetColumnReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary;
    function GetColumnReverseOrder(AColumn: Integer): Integer;
    function GetColumnWidthDictionary: TTMSFNCDataGridDoubleDictionary;
    function GetColumnWidth(AColumn: integer): Double;
    function GetFilteredRowDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetFilteredRow(ARow: Integer): Boolean;
    function GetHasRowOrder(ARow: Integer): Boolean;
    function GetHeight: Single;
    function GetHiddenColumnDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetHiddenColumn(AColumn: Integer): Boolean;
    function GetHiddenColumnWidth(AColumn: Integer): Double;
    function GetHiddenRowDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetHiddenRowHeight(ARow: Integer): Double;
    function GetHiddenRow(ARow: Integer): Boolean;
    function GetMergedColumnDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetMergedColumn(AColumn: Integer): Boolean;
    function GetMergedRowDictionary: TTMSFNCDataGridBooleanDictionary;
    function GetMergedRow(ARow: Integer): Boolean;
    function GetRowHeightDictionary: TTMSFNCDataGridDoubleDictionary;
    function GetRowHeight(ARow: Integer): Double;
    function GetRowOrderDictionary: TTMSFNCDataGridIntegerDictionary;
    function GetRowOrder(ARow: Integer): Integer;
    function GetRowPositionDictionary: TTMSFNCDataGridDoubleDictionary;
    function GetRowPosition(ARow: Integer): Double;
    function GetRowReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary;
    function GetRowReverseOrder(ARow: Integer): Integer;
    function GetWidth: Single;
    function IsDefaultColumnWidthStored: Boolean;
    function IsDefaultRowHeightStored: Boolean;
    procedure SetColumnCount(const Value: Integer);
    procedure SetColumnWidth(AColumn: Integer; const Value: Double);
    procedure SetColumnPosition(AColumn: Integer; const Value: Double);
    procedure SetDefaultColumnWidth(const Value: Double);
    procedure SetDefaultRowHeight(const Value: Double);
    procedure SetFilteredRow(ARow: Integer; const Value: Boolean);
    procedure SetFixedBottomRowCount(const Value: Integer);
    procedure SetFixedColumnCount(const Value: Integer);
    procedure SetFixedRightColumnCount(const Value: Integer);
    procedure SetFixedRowCount(const Value: Integer);
    procedure SetFreezeColumnCount(const Value: Integer);
    procedure SetFreezeRowCount(const Value: Integer);
    procedure SetHiddenColumn(AColumn: Integer; const Value: Boolean);
    procedure SetHiddenRow(ARow: Integer; const Value: Boolean);
    procedure SetMergedColumn(AColumn: Integer; const Value: Boolean);
    procedure SetMergedRow(ARow: Integer; const Value: Boolean);
    procedure SetRowCount(const Value: Integer);
    procedure SetRowHeight(ARow: Integer; const Value: Double);
    procedure SetRowPosition(ARow: Integer; const Value: Double);
    procedure SetWidth(const Value: Single);
    procedure SetHeight(const Value: Single);
  protected
    function FormatArray(AArray: TArray<Integer>): string;
    function FormatNumberRange(AStartNumber, ACount: Integer): string;
    function CanRegisterRuntimeClasses: Boolean; virtual;
    function GetContentClipRect: TRectF; virtual;
    function GetContentRect: TRectF; virtual;
    function GetFilteredRowCount: Integer;
    function GetHiddenColumnCount: Integer;
    function GetHiddenRowCount: Integer;
    function GetHorizontalScrollPosition: Double; virtual;
    function GetIsDestroying: Boolean; virtual;
    function GetIsLoading: Boolean; virtual;
    function GetStartColumn: Integer;
    function GetStartRow: Integer;
    function GetStopColumn: Integer;
    function GetStopRow: Integer;
    function GetTotalColumnWidth: Double;
    function GetTotalRowHeight: Double;
    function GetUpdateCount: Integer;
    function GetVerticalScrollPosition: Double; virtual;
    function GetVisibleColumnCount: Integer;
    function GetVisibleRowCount: Integer;
    function IsBlocked: Boolean; virtual;
    function LocalRect: TRectF; virtual;
    function OnlyLastFullVisibleColumn: Boolean; virtual;
    function OnlyLastFullVisibleRow: Boolean; virtual;
    procedure RegisterRuntimeClasses; virtual;
    procedure DecUpdateCount;
    procedure DoUpdate; virtual;
    procedure IncUpdateCount;
    procedure InternalClear; virtual;
    procedure SetColumnOrder(AColumn: Integer; const Value: Integer);
    procedure SetRowOrder(ARow: Integer; const Value: Integer);
    procedure SynchronizeColumns(ACollectionUpdate: Boolean = False); virtual;
    procedure DoCanSaveProperty(AObject: TObject; APropertyName: string; APropertyType: TTypeKind; var ACanSave: Boolean); virtual;
    procedure DoCanLoadProperty(AObject: TObject; APropertyName: string; APropertyType: TTypeKind; var ACanLoad: Boolean); virtual;
    class property BlockPersistenceInterface: Boolean read FBlockPersistenceInterface write FBlockPersistenceInterface;
  public
    procedure Assign(Source: TPersistent); override;
    constructor Create; virtual;
    destructor Destroy; override;

    function IsAppearanceProperty({%H-}AObject: TObject; {%H-}APropertyName: string; {%H-}APropertyType: TTypeKind): Boolean; virtual;
    function CanSaveProperty({%H-}AObject: TObject; {%H-}APropertyName: string; {%H-}APropertyType: TTypeKind): Boolean; virtual;
    function CanLoadProperty({%H-}AObject: TObject; {%H-}APropertyName: string; {%H-}APropertyType: TTypeKind): Boolean; virtual;
    function DisplayToRealCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function GetTotalFixedBottomRowHeight: Double;
    function GetTotalFixedColumnWidth(AIncludeFreeze: Boolean = False): Double;
    function GetTotalFixedRightColumnWidth: Double;
    function GetTotalFixedRowHeight(AIncludeFreeze: Boolean = False): Double;
    function IsCellDisabled(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellDisplayed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixedColumn(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixedRow(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFixedRoot(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreeze(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreezeFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellFreezeRoot(ACell: TTMSFNCDataGridCellCoord): Boolean;
    function IsCellValid(ACell: TTMSFNCDataGridCellCoord): Boolean; overload;
    function IsCellValid(AColumn, ARow: Integer): Boolean; overload;
    function IsCellVisible(ACell: TTMSFNCDataGridCellCoord): Boolean; virtual; abstract;
    function IsColumnHidden(AColumn: Integer): Boolean;
    function IsColumnVisible(AColumn: Integer): Boolean;
    function IsColumnMerged(AColumn: Integer): Boolean;
    function IsColumnValid(AColumn: Integer): Boolean;
    function IsRowFiltered(ARow: Integer): Boolean; virtual;
    function IsRowHidden(ARow: Integer): Boolean;
    function IsRowVisible(ARow: Integer): Boolean;
    function IsRowMerged(ARow: Integer): Boolean;
    function IsRowValid(ARow: Integer): Boolean;
    function RealToDisplayCell(ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
    function StretchColumns(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewWidth: Double = -1): Boolean;
    function StretchRows(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewHeight: Double = -1): Boolean;
    procedure AfterExport; virtual;
    procedure BeforeExport; virtual;
    procedure SetExportRect(ARect: TRectF); virtual;
    procedure BeginUpdate; virtual;
    procedure Clear; virtual;
    procedure ClearRowInfo(ARow: Integer = -1); virtual;
    procedure ClearColumnInfo(AColumn: Integer = -1); virtual;
    procedure EndUpdate; virtual;
    procedure ExportNotification(AState: TTMSFNCDataGridExportState; ARow: Integer); virtual;
    procedure HideColumn(AColumn: Integer);
    procedure HideRow(ARow: Integer);
    procedure ImportNotification(AState: TTMSFNCDataGridImportState; ARow: Integer); virtual;
    procedure LogMessage(AMessage: string; AMessageType: TTMSFNCDataGridLogMessageType = glmInfo);
    procedure SetLocalRect(ARect: TRectF); virtual;
    procedure SaveSettingsToFile(AFileName: string; AAppearanceOnly: Boolean = False); virtual;
    procedure LoadSettingsFromFile(AFileName: string); virtual;
    procedure SaveSettingsToStream(AStream: TStream; AAppearanceOnly: Boolean = False); virtual;
    procedure LoadSettingsFromStream(AStream: TStream); virtual;
    procedure UnhideAll;
    procedure UnhideAllColumns;
    procedure UnhideAllRows;
    procedure UnhideColumn(AColumn: Integer);
    procedure UnhideRow(ARow: Integer);
    procedure Update; virtual;
    procedure UpdateColumnRowCalculations; virtual;
    procedure UpdateVisualRange; virtual;
    property ColumnOrders[AColumn: Integer]: Integer read GetColumnOrder;
    property ColumnPositions[AColumn: Integer]: Double read GetColumnPosition;
    property ColumnReverseOrders[AColumn: Integer]: Integer read GetColumnReverseOrder;
    property ColumnWidths[AColumn: Integer]: Double read GetColumnWidth write SetColumnWidth;
    property FilteredRows[ARow: Integer]: Boolean read GetFilteredRow write SetFilteredRow;
    property FilteredRowCount: Integer read GetFilteredRowCount;
    property HasRowOrder[ARow: Integer]: Boolean read GetHasRowOrder;
    property Height: Single read GetHeight write SetHeight;
    property HiddenColumnCount: Integer read GetHiddenColumnCount;
    property HiddenColumns[AColumn: Integer]: Boolean read GetHiddenColumn write SetHiddenColumn;
    property HiddenColumnWidths[AColumn: Integer]: Double read GetHiddenColumnWidth;
    property HiddenRowCount: Integer read GetHiddenRowCount;
    property HiddenRowHeights[ARow: Integer]: Double read GetHiddenRowHeight;
    property HiddenRows[ARow: Integer]: Boolean read GetHiddenRow write SetHiddenRow;
    property IsExporting: Boolean read FIsExporting;
    property MergedColumns[AColumn: Integer]: Boolean read GetMergedColumn write SetMergedColumn;
    property MergedRows[ARow: Integer]: Boolean read GetMergedRow write SetMergedRow;
    property Name: string read FName write FName;
    property OnCanSaveProperty: TTMSFNCCustomControlCanSavePropertyEvent read FOnCanSaveProperty write FOnCanSaveProperty;
    property OnCanLoadProperty: TTMSFNCCustomControlCanLoadPropertyEvent read FOnCanLoadProperty write FOnCanLoadProperty;
    property OnLogMessage: TTMSFNCDataGridLogMessageEvent read FOnLogMessage write FOnLogMessage;
    property RowHeights[ARow: Integer]: Double read GetRowHeight write SetRowHeight;
    property RowOrders[ARow: Integer]: Integer read GetRowOrder;
    property RowPositions[ARow: Integer]: Double read GetRowPosition;
    property RowReverseOrders[ARow: Integer]: Integer read GetRowReverseOrder;
    property StartColumn: Integer read GetStartColumn;
    property StartRow: Integer read GetStartRow;
    property StopColumn: Integer read GetStopColumn;
    property StopRow: Integer read GetStopRow;
    property TotalColumnWidth: Double read FTotalColumnWidth;
    property TotalFixedBottomRowHeight: Double read FTotalFixedBottomRowHeight;
    property TotalFixedColumnWidth: Double read FTotalFixedColumnWidth;
    property TotalFixedRightColumnWidth: Double read FTotalFixedRightColumnWidth;
    property TotalFixedRowHeight: Double read FTotalFixedRowHeight;
    property TotalRowHeight: Double read FTotalRowHeight;
    property UpdateCount: Integer read GetUpdateCount;
    property VisibleColumnCount: Integer read GetVisibleColumnCount;
    property VisibleRowCount: Integer read GetVisibleRowCount;
    property Width: Single read GetWidth write SetWidth;
  published
    property ColumnCount: Integer read FColumnCount write SetColumnCount default 4;
    property ColumnOrderDictionary: TTMSFNCDataGridIntegerDictionary read GetColumnOrderDictionary;
    property ColumnPositionDictionary: TTMSFNCDataGridDoubleDictionary read GetColumnPositionDictionary;
    property ColumnReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary read GetColumnReverseOrderDictionary;
    property ColumnWidthDictionary: TTMSFNCDataGridDoubleDictionary read GetColumnWidthDictionary;
    property DefaultColumnWidth: Double read FDefaultColumnWidth write SetDefaultColumnWidth stored IsDefaultColumnWidthStored nodefault;
    property DefaultRowHeight: Double read FDefaultRowHeight write SetDefaultRowHeight stored IsDefaultRowHeightStored nodefault;
    property FilteredRowDictionary: TTMSFNCDataGridBooleanDictionary read GetFilteredRowDictionary;
    property FixedBottomRowCount: Integer read FFixedBottomRowCount write SetFixedBottomRowCount default 0;
    property FixedColumnCount: Integer read FFixedColumnCount write SetFixedColumnCount default 0;
    property FixedRightColumnCount: Integer read FFixedRightColumnCount write SetFixedRightColumnCount default 0;
    property FixedRowCount: Integer read FFixedRowCount write SetFixedRowCount default 1;
    property FreezeColumnCount: Integer read FFreezeColumnCount write SetFreezeColumnCount default 0;
    property FreezeRowCount: Integer read FFreezeRowCount write SetFreezeRowCount default 0;
    property HiddenColumnDictionary: TTMSFNCDataGridBooleanDictionary read GetHiddenColumnDictionary;
    property HiddenRowDictionary: TTMSFNCDataGridBooleanDictionary read GetHiddenRowDictionary;
    property MergedColumnDictionary: TTMSFNCDataGridBooleanDictionary read GetMergedColumnDictionary;
    property MergedRowDictionary: TTMSFNCDataGridBooleanDictionary read GetMergedRowDictionary;
    property RowCount: Integer read FRowCount write SetRowCount default 24;
    property RowHeightDictionary: TTMSFNCDataGridDoubleDictionary read GetRowHeightDictionary;
    property RowOrderDictionary: TTMSFNCDataGridIntegerDictionary read GetRowOrderDictionary;
    property RowPositionDictionary: TTMSFNCDataGridDoubleDictionary read GetRowPositionDictionary;
    property RowReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary read GetRowReverseOrderDictionary;
  end;

function MakeCell(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
function MakeCellRange(AStartColumn, AStartRow, AStopColumn, AStopRow: Integer): TTMSFNCDataGridCellCoordRange;

implementation

uses
  TypInfo, DateUtils, WEBLib.Controls, WEBLib.TMSFNCDataGridCell, WEBLib.Forms, Math, WEBLib.TMSFNCUtils
  {$IFNDEF WEBLIB}
  ,SysUtils
  {$ENDIF}
  {$IFDEF FMXLIB}
  ,FMX.Types
  {$ENDIF}
  ;

function MakeCellRange(AStartColumn, AStartRow, AStopColumn, AStopRow: Integer): TTMSFNCDataGridCellCoordRange;
begin
  Result.StartCell := MakeCell(AStartColumn, AStartRow);
  Result.StopCell := MakeCell(AStopColumn, AStopRow);
end;

function MakeCell(AColumn, ARow: Integer): TTMSFNCDataGridCellCoord;
begin
  Result.Column := AColumn;
  Result.Row := ARow;
end;

{ TTMSFNCDataGridCore }

procedure TTMSFNCDataGridCore.AfterExport;
begin
  FIsExporting := False;
  FLocalRect := FSaveLocalRect;
  FExportRectSet := False;
  Update;
end;

procedure TTMSFNCDataGridCore.Assign(Source: TPersistent);
begin
  BeginUpdate;
  try
    if Source is TTMSFNCDataGridCore then
    begin
      FColumnCount := (Source as TTMSFNCDataGridCore).ColumnCount;
      FreeAndNil(FColumnOrderDictionary);
      FColumnOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create((Source as TTMSFNCDataGridCore).ColumnOrderDictionary);
      FreeAndNil(FColumnPositionDictionary);
      FColumnPositionDictionary := TTMSFNCDataGridDoubleDictionary.Create((Source as TTMSFNCDataGridCore).ColumnPositionDictionary);
      FreeAndNil(FColumnReverseOrderDictionary);
      FColumnReverseOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create((Source as TTMSFNCDataGridCore).ColumnReverseOrderDictionary);
      FreeAndNil(FColumnWidthDictionary);
      FColumnWidthDictionary := TTMSFNCDataGridDoubleDictionary.Create((Source as TTMSFNCDataGridCore).ColumnWidthDictionary);
      FDefaultColumnWidth := (Source as TTMSFNCDataGridCore).DefaultColumnWidth;
      FDefaultRowHeight := (Source as TTMSFNCDataGridCore).DefaultRowHeight;
      FreeAndNil(FFilteredRowDictionary);
      FFilteredRowDictionary := TTMSFNCDataGridBooleanDictionary.Create((Source as TTMSFNCDataGridCore).FilteredRowDictionary);

      FFixedBottomRowCount := (Source as TTMSFNCDataGridCore).FixedBottomRowCount;
      FFixedColumnCount := (Source as TTMSFNCDataGridCore).FixedColumnCount;
      FFixedRightColumnCount := (Source as TTMSFNCDataGridCore).FixedRightColumnCount;
      FFixedRowCount := (Source as TTMSFNCDataGridCore).FixedRowCount;
      FFreezeColumnCount := (Source as TTMSFNCDataGridCore).FreezeColumnCount;
      FFreezeRowCount := (Source as TTMSFNCDataGridCore).FreezeRowCount;

      FreeAndNil(FHiddenColumnDictionary);
      FHiddenColumnDictionary := TTMSFNCDataGridBooleanDictionary.Create((Source as TTMSFNCDataGridCore).HiddenColumnDictionary);
      FreeAndNil(FHiddenRowDictionary);
      FHiddenRowDictionary := TTMSFNCDataGridBooleanDictionary.Create((Source as TTMSFNCDataGridCore).HiddenRowDictionary);
      FreeAndNil(FMergedColumnDictionary);
      FMergedColumnDictionary := TTMSFNCDataGridBooleanDictionary.Create((Source as TTMSFNCDataGridCore).MergedColumnDictionary);
      FreeAndNil(FMergedRowDictionary);
      FMergedRowDictionary := TTMSFNCDataGridBooleanDictionary.Create((Source as TTMSFNCDataGridCore).MergedRowDictionary);

      FRowCount := (Source as TTMSFNCDataGridCore).RowCount;
      FreeAndNil(FRowOrderDictionary);
      FRowOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create((Source as TTMSFNCDataGridCore).RowOrderDictionary);
      FreeAndNil(FRowPositionDictionary);
      FRowPositionDictionary := TTMSFNCDataGridDoubleDictionary.Create((Source as TTMSFNCDataGridCore).RowPositionDictionary);
      FreeAndNil(FRowReverseOrderDictionary);
      FRowReverseOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create((Source as TTMSFNCDataGridCore).RowReverseOrderDictionary);
      FreeAndNil(FRowHeightDictionary);
      FRowHeightDictionary := TTMSFNCDataGridDoubleDictionary.Create((Source as TTMSFNCDataGridCore).RowHeightDictionary);
    end
    else
      inherited;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetExportRect(ARect: TRectF);
begin
  FExportRect := ARect;
  FExportRectSet := True;
end;

procedure TTMSFNCDataGridCore.BeforeExport;
begin
  FIsExporting := True;
  FSaveLocalRect := FLocalRect;
  Update;
end;

procedure TTMSFNCDataGridCore.BeginUpdate;
begin
  Inc(FUpdateCount);
end;

procedure TTMSFNCDataGridCore.Clear;
begin
  BeginUpdate;
  try
    InternalClear;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.ClearColumnInfo(AColumn: Integer = -1);
begin
  BeginUpdate;
  try
    if AColumn = -1 then
    begin
      ColumnReverseOrderDictionary.Clear;
      ColumnOrderDictionary.Clear;
      MergedColumnDictionary.Clear;
      ColumnWidthDictionary.Clear;
      ColumnPositionDictionary.Clear;
      HiddenColumnDictionary.Clear;
    end
    else
    begin
      ColumnReverseOrderDictionary.Remove(AColumn);
      ColumnOrderDictionary.Remove(AColumn);
      MergedColumnDictionary.Remove(AColumn);
      ColumnWidthDictionary.Remove(AColumn);
      ColumnPositionDictionary.Remove(AColumn);
      HiddenColumnDictionary.Remove(AColumn);
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.ClearRowInfo(ARow: Integer = -1);
begin
  BeginUpdate;
  try
    if ARow = -1 then
    begin
      RowReverseOrderDictionary.Clear;
      RowOrderDictionary.Clear;
      MergedRowDictionary.Clear;
      FilteredRowDictionary.Clear;
      RowHeightDictionary.Clear;
      RowPositionDictionary.Clear;
      HiddenRowDictionary.Clear;
    end
    else
    begin
      RowReverseOrderDictionary.Remove(ARow);
      RowOrderDictionary.Remove(ARow);
      MergedRowDictionary.Remove(ARow);
      FilteredRowDictionary.Remove(ARow);
      RowHeightDictionary.Remove(ARow);
      RowPositionDictionary.Remove(ARow);
      HiddenRowDictionary.Remove(ARow);
    end;
  finally
    EndUpdate;
  end;
end;

constructor TTMSFNCDataGridCore.Create;
begin
  FFreezeRowCount := 0;
  FFreezeColumnCount := 0;
  FFixedRowCount := 1;
  FFixedColumnCount := 0;
  FFixedBottomRowCount := 0;
  FFixedRightColumnCount := 0;
  FDefaultRowHeight := 30;
  FDefaultColumnWidth := 75;
  FColumnCount := 4;
  FRowCount := 24;

  if CanRegisterRuntimeClasses then
    RegisterRuntimeClasses;
end;

procedure TTMSFNCDataGridCore.DecUpdateCount;
begin
  Dec(FUpdateCount);
end;

destructor TTMSFNCDataGridCore.Destroy;
begin
  FreeAndNil(FRowReverseOrderDictionary);
  FreeAndNil(FColumnReverseOrderDictionary);
  FreeAndNil(FRowOrderDictionary);
  FreeAndNil(FColumnOrderDictionary);
  FreeAndNil(FMergedColumnDictionary);
  FreeAndNil(FMergedRowDictionary);
  FreeAndNil(FFilteredRowDictionary);
  FreeAndNil(FHiddenColumnDictionary);
  FreeAndNil(FHiddenRowDictionary);
  FreeAndNil(FColumnPositionDictionary);
  FreeAndNil(FRowPositionDictionary);
  FreeAndNil(FColumnWidthDictionary);
  FreeAndNil(FRowHeightDictionary);
  inherited;
end;

function TTMSFNCDataGridCore.IsAppearanceProperty(AObject: TObject; APropertyName: string; APropertyType: TTypeKind): Boolean;
var
  Prop: TTMSFNCPropertyInfo;
  pName, cn: string;
begin
  Result := False;
  Prop := GetPropInfo(AObject, APropertyName);
  if Assigned(Prop) then
  begin
    pName := TTMSFNCPersistence.GetPropInfoName(Prop);

    cn := TTMSFNCPersistence.GetPropInfoTypeName(Prop);
    Result := (cn = 'TAlphaColor') or (cn = 'TColor') or (cn = 'TGraphicsColor') or
      (cn = 'TTMSFNCGraphicsFill') or (cn = 'TTMSFNCGraphicsStroke') or (Pos('Appearance', pName) > 0);

    Result := Result or (APropertyName = 'Fill') or (APropertyName = 'Stroke') or (APropertyName = 'Font');
  end;
end;

function TTMSFNCDataGridCore.CanSaveProperty(AObject: TObject; APropertyName: string; APropertyType: TTypeKind): Boolean;
begin
  if (AObject = Self) and not BlockPersistenceInterface then
    Result := (TTMSFNCUtils.IndexOfTextInArray(APropertyName, ExcludePropertyList) = -1)
  else
    Result := True;

  if FAppearancePersisting then
    Result := Result and IsAppearanceProperty(AObject, APropertyName, APropertyType);

  DoCanSaveProperty(AObject, APropertyName, APropertyType, Result);
end;

function TTMSFNCDataGridCore.CanLoadProperty(AObject: TObject; APropertyName: string; APropertyType: TTypeKind): Boolean;
begin
  if (AObject = Self) and not BlockPersistenceInterface then
    Result := (TTMSFNCUtils.IndexOfTextInArray(APropertyName, ExcludePropertyList) = -1)
  else
    Result := True;

  DoCanLoadProperty(AObject, APropertyName, APropertyType, Result);
end;

function TTMSFNCDataGridCore.CanRegisterRuntimeClasses: Boolean;
begin
  Result := True;
end;

procedure TTMSFNCDataGridCore.SaveSettingsToFile(AFileName: string; AAppearanceOnly: Boolean = False);
begin
  FAppearancePersisting := AAppearanceOnly;
  TTMSFNCPersistence.SaveSettingsToFile(Self, AFileName);
  FAppearancePersisting := False;
end;

procedure TTMSFNCDataGridCore.LoadSettingsFromFile(AFileName: string);
begin
  BeginUpdate;
  TTMSFNCPersistence.LoadSettingsFromFile(Self, AFileName);
  EndUpdate;
end;

procedure TTMSFNCDataGridCore.SaveSettingsToStream(AStream: TStream; AAppearanceOnly: Boolean = False);
begin
  FAppearancePersisting := AAppearanceOnly;
  TTMSFNCPersistence.SaveSettingsToStream(Self, AStream);
  FAppearancePersisting := False;
end;

procedure TTMSFNCDataGridCore.LoadSettingsFromStream(AStream: TStream);
begin
  BeginUpdate;
  TTMSFNCPersistence.LoadSettingsFromStream(Self, AStream);
  EndUpdate;
end;

function TTMSFNCDataGridCore.DisplayToRealCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := MakeCell(ColumnReverseOrders[ACell.Column], RowReverseOrders[ACell.Row]);
end;

function TTMSFNCDataGridCore.LocalRect: TRectF;
begin
  if IsExporting and FExportRectSet then
    Result := FExportRect
  else
    Result := FLocalRect;

  if RectIsEmpty(Result) and (Name <> 'Root') then
    Result := RectF(0, 0, TotalColumnWidth, TotalRowHeight);
end;

function TTMSFNCDataGridCore.GetVerticalScrollPosition: Double;
begin
  Result := 0;
end;

function TTMSFNCDataGridCore.GetHorizontalScrollPosition: Double;
begin
  Result := 0;
end;

function TTMSFNCDataGridCore.GetIsDestroying: Boolean;
begin
  Result := False;
end;

function TTMSFNCDataGridCore.GetIsLoading: Boolean;
begin
  Result := False;
end;

function TTMSFNCDataGridCore.GetContentClipRect: TRectF;
begin
  Result := GetContentRect;
  Result.Bottom := Result.Top + Min(TotalRowHeight, Result.Bottom - Result.Top);
  Result.Right := Result.Left + Min(TotalColumnWidth, Result.Right - Result.Left);
end;

function TTMSFNCDataGridCore.GetContentRect: TRectF;
begin
  Result := LocalRect;
end;

function TTMSFNCDataGridCore.GetFilteredRowDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FFilteredRowDictionary) then
    FFilteredRowDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FFilteredRowDictionary;
end;

function TTMSFNCDataGridCore.GetFilteredRowCount: Integer;
var
  k: Integer;
begin
  Result := 0;
  for k in FilteredRowDictionary.Keys do
  begin
    if IsRowValid(k) then
      Inc(Result);
  end;
end;

function TTMSFNCDataGridCore.GetFilteredRow(ARow: Integer): Boolean;
begin
  if FilteredRowDictionary.ContainsKey(ARow) then
    Result := FilteredRowDictionary[ARow]
  else
    Result := False;
end;

function TTMSFNCDataGridCore.GetMergedColumnDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FMergedColumnDictionary) then
    FMergedColumnDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FMergedColumnDictionary;
end;

function TTMSFNCDataGridCore.GetMergedColumn(AColumn: Integer): Boolean;
begin
  if MergedColumnDictionary.ContainsKey(AColumn) then
    Result := MergedColumnDictionary[AColumn]
  else
    Result := False;
end;

function TTMSFNCDataGridCore.GetMergedRowDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FMergedRowDictionary) then
    FMergedRowDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FMergedRowDictionary;
end;

function TTMSFNCDataGridCore.GetMergedRow(ARow: Integer): Boolean;
begin
  if MergedRowDictionary.ContainsKey(ARow) then
    Result := MergedRowDictionary[ARow]
  else
    Result := False;
end;

function TTMSFNCDataGridCore.GetHasRowOrder(ARow: Integer): Boolean;
begin
  Result := RowOrderDictionary.ContainsKey(ARow);
end;

function TTMSFNCDataGridCore.GetHeight: Single;
var
  r: TRectF;
begin
  r := FLocalRect;
  Result := r.Bottom - r.Top;
end;

function TTMSFNCDataGridCore.GetHiddenColumnDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FHiddenColumnDictionary) then
    FHiddenColumnDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FHiddenColumnDictionary;
end;

function TTMSFNCDataGridCore.GetHiddenColumn(AColumn: Integer): Boolean;
begin
  if HiddenColumnDictionary.ContainsKey(AColumn) then
    Result := HiddenColumnDictionary[AColumn]
  else
    Result := False;
end;

function TTMSFNCDataGridCore.GetHiddenColumnWidth(AColumn: Integer): Double;
begin
  if ColumnWidthDictionary.ContainsKey(AColumn) then
    Result := ColumnWidthDictionary[AColumn]
  else
    Result := DefaultColumnWidth;
end;

function TTMSFNCDataGridCore.GetHiddenRowDictionary: TTMSFNCDataGridBooleanDictionary;
begin
  if not Assigned(FHiddenRowDictionary) then
    FHiddenRowDictionary := TTMSFNCDataGridBooleanDictionary.Create;

  Result := FHiddenRowDictionary;
end;

function TTMSFNCDataGridCore.GetHiddenRow(ARow: Integer): Boolean;
begin
  if HiddenRowDictionary.ContainsKey(ARow) then
    Result := HiddenRowDictionary[ARow]
  else
    Result := False;
end;

function TTMSFNCDataGridCore.GetRowHeightDictionary: TTMSFNCDataGridDoubleDictionary;
begin
  if not Assigned(FRowHeightDictionary) then
    FRowHeightDictionary := TTMSFNCDataGridDoubleDictionary.Create;

  Result := FRowHeightDictionary;
end;

function TTMSFNCDataGridCore.GetRowHeight(ARow: Integer): Double;
begin
  if RowHeightDictionary.ContainsKey(ARow) then
    Result := RowHeightDictionary[ARow]
  else
    Result := DefaultRowHeight;

  if IsRowHidden(ARow) then
    Result := 0;
end;

function TTMSFNCDataGridCore.GetRowOrderDictionary: TTMSFNCDataGridIntegerDictionary;
begin
  if not Assigned(FRowOrderDictionary) then
    FRowOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create;

  Result := FRowOrderDictionary;
end;

function TTMSFNCDataGridCore.GetRowReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary;
begin
  if not Assigned(FRowReverseOrderDictionary) then
    FRowReverseOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create;

  Result := FRowReverseOrderDictionary;
end;

function TTMSFNCDataGridCore.GetRowReverseOrder(ARow: Integer): Integer;
begin
  if RowReverseOrderDictionary.ContainsKey(ARow) then
    Result := RowReverseOrderDictionary[ARow]
  else
    Result := ARow;
end;

function TTMSFNCDataGridCore.GetRowOrder(ARow: Integer): Integer;
begin
  if RowOrderDictionary.ContainsKey(ARow) then
    Result := RowOrderDictionary[ARow]
  else
  begin
    SetRowOrder(ARow, ARow);
    Result := ARow;
  end;
end;

function TTMSFNCDataGridCore.GetRowPositionDictionary: TTMSFNCDataGridDoubleDictionary;
begin
  if not Assigned(FRowPositionDictionary) then
    FRowPositionDictionary := TTMSFNCDataGridDoubleDictionary.Create;

  Result := FRowPositionDictionary;
end;

function TTMSFNCDataGridCore.GetRowPosition(ARow: Integer): Double;
var
  I: Integer;
begin
  if RowPositionDictionary.ContainsKey(ARow) then
    Result := RowPositionDictionary[ARow]
  else
  begin
    Result := 0;
    if ARow > RowCount div 2 then
    begin
      for I := RowCount - 1 downto ARow do
        Result := Result + RowHeights[I];

      Result := TotalRowHeight - Result;
    end
    else
    begin
      for I := 0 to ARow - 1 do
        Result := Result + RowHeights[I];
    end;

    SetRowPosition(ARow, Result);
  end;
end;

function TTMSFNCDataGridCore.GetTotalColumnWidth: Double;
begin
  Result := TotalColumnWidth;
end;

function TTMSFNCDataGridCore.GetTotalFixedBottomRowHeight: Double;
var
  I: Integer;
begin
  Result := 0;
  for I := RowCount - 1 downto RowCount - FixedBottomRowCount do
    Result := Result + RowHeights[I];
end;

function TTMSFNCDataGridCore.GetTotalFixedColumnWidth(AIncludeFreeze: Boolean = False): Double;
var
  I: Integer;
  c: Integer;
begin
  Result := 0;
  if AIncludeFreeze then
    c := FixedColumnCount + FreezeColumnCount - 1
  else
    c := FixedColumnCount - 1;

  for I := 0 to c do
    Result := Result + ColumnWidths[I];
end;

function TTMSFNCDataGridCore.GetTotalFixedRightColumnWidth: Double;
var
  I: Integer;
begin
  Result := 0;
  for I := ColumnCount - 1 downto ColumnCount - FixedRightColumnCount do
    Result := Result + ColumnWidths[I];
end;

function TTMSFNCDataGridCore.GetTotalFixedRowHeight(AIncludeFreeze: Boolean = False): Double;
var
  I: Integer;
  r: Integer;
begin
  Result := 0;
  if AIncludeFreeze then
    r := FixedRowCount + FreezeRowCount - 1
  else
    r := FixedRowCount - 1;

  for I := 0 to r do
    Result := Result + RowHeights[I];
end;

function TTMSFNCDataGridCore.GetTotalRowHeight: Double;
begin
  Result := TotalRowHeight;
end;

function TTMSFNCDataGridCore.IsColumnValid(AColumn: Integer): Boolean;
begin
  Result := (AColumn >= 0) and (AColumn <= ColumnCount - 1);
end;

function TTMSFNCDataGridCore.IsColumnVisible(AColumn: Integer): Boolean;
begin
  Result := not IsColumnHidden(AColumn);
end;

function TTMSFNCDataGridCore.IsDefaultColumnWidthStored: Boolean;
begin
  Result := DefaultColumnWidth <> 75;
end;

function TTMSFNCDataGridCore.IsDefaultRowHeightStored: Boolean;
begin
  Result := DefaultRowHeight <> 30;
end;

function TTMSFNCDataGridCore.IsRowValid(ARow: Integer): Boolean;
begin
  Result := (ARow >= 0) and (ARow <= RowCount - 1);
end;

function TTMSFNCDataGridCore.IsRowVisible(ARow: Integer): Boolean;
begin
  Result := not IsRowHidden(ARow);
end;

function TTMSFNCDataGridCore.IsCellValid(AColumn, ARow: Integer): Boolean;
begin
  Result := (AColumn >= 0) and (AColumn <= ColumnCount - 1) and (ARow >= 0) and (ARow <= RowCount - 1);
end;

function TTMSFNCDataGridCore.IsCellValid(ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := IsCellValid(ACell.Column, ACell.Row)
end;

function TTMSFNCDataGridCore.IsCellFixed(ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
  begin
    Result := (ACell.Row < FixedRowCount) or (ACell.Row > RowCount - 1 - FixedBottomRowCount)
      or (ACell.Column < FixedColumnCount) or (ACell.Column > ColumnCount - 1 - FixedRightColumnCount);
  end;
end;

function TTMSFNCDataGridCore.IsCellFixedColumn(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
    Result := (ACell.Column < FixedColumnCount) or (ACell.Column > ColumnCount - 1 - FixedRightColumnCount);
end;

function TTMSFNCDataGridCore.IsCellFixedRoot(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
  begin
    Result := ((ACell.Column < FixedColumnCount) and ((ACell.Row < FixedRowCount) or (ACell.Row > RowCount - 1 - FixedBottomRowCount)))
      or ((ACell.Column > ColumnCount - 1 - FixedRightColumnCount) and ((ACell.Row < FixedRowCount) or (ACell.Row > RowCount - 1 - FixedBottomRowCount)));
  end;
end;

function TTMSFNCDataGridCore.IsCellFixedRow(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
    Result := (ACell.Row < FixedRowCount) or (ACell.Row > RowCount - 1 - FixedBottomRowCount);
end;

function TTMSFNCDataGridCore.IsCellFreeze(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
  begin
    if (ACell.Column >= FixedColumnCount) and (ACell.Column < FixedColumnCount + FreezeColumnCount) then
      Result := (ACell.Row >= FixedRowCount)
    else if (ACell.Row >= FixedRowCount) and (ACell.Row < FixedRowCount + FreezeRowCount) then
      Result := (ACell.Column >= FixedColumnCount);
  end;
end;

function TTMSFNCDataGridCore.IsCellFreezeFixed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
  begin
    if (ACell.Column < FixedColumnCount) or (ACell.Column > ColumnCount - 1 - FixedRightColumnCount) then
      Result := (ACell.Row >= FixedRowCount) and (ACell.Row < FixedRowCount + FreezeRowCount)
    else if (ACell.Row < FixedRowCount) or (ACell.Row > RowCount - 1 - FixedBottomRowCount) then
      Result := (ACell.Column >= FixedColumnCount) and (ACell.Column < FixedColumnCount + FreezeColumnCount)
  end;
end;

function TTMSFNCDataGridCore.IsCellFreezeRoot(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellValid(ACell) then
  begin
    Result := (ACell.Column >= FixedColumnCount) and (ACell.Column < FixedColumnCount + FreezeColumnCount);
    Result := Result and (ACell.Row >= FixedRowCount) and (ACell.Row < FixedRowCount + FreezeRowCount);
  end;
end;

function TTMSFNCDataGridCore.IsColumnMerged(AColumn: Integer): Boolean;
begin
  Result := MergedColumns[AColumn];
end;

function TTMSFNCDataGridCore.IsRowMerged(ARow: Integer): Boolean;
begin
  Result := MergedRows[ARow];
end;

function TTMSFNCDataGridCore.IsColumnHidden(AColumn: Integer): Boolean;
begin
  Result := HiddenColumns[AColumn];
end;

function TTMSFNCDataGridCore.IsRowFiltered(ARow: Integer): Boolean;
begin
  Result := FilteredRows[ARow];
end;

function TTMSFNCDataGridCore.IsRowHidden(ARow: Integer): Boolean;
begin
  Result := HiddenRows[ARow] or IsRowFiltered(ARow);
end;

procedure TTMSFNCDataGridCore.ImportNotification(
  AState: TTMSFNCDataGridImportState; ARow: Integer);
begin

end;

procedure TTMSFNCDataGridCore.IncUpdateCount;
begin
  Inc(FUpdateCount);
end;

procedure TTMSFNCDataGridCore.InternalClear;
begin
  ColumnReverseOrderDictionary.Clear;
  RowReverseOrderDictionary.Clear;
  ColumnOrderDictionary.Clear;
  RowOrderDictionary.Clear;
  MergedColumnDictionary.Clear;
  MergedRowDictionary.Clear;
  FilteredRowDictionary.Clear;
  ColumnWidthDictionary.Clear;
  RowHeightDictionary.Clear;
  RowPositionDictionary.Clear;
  ColumnPositionDictionary.Clear;
  HiddenColumnDictionary.Clear;
  HiddenRowDictionary.Clear;
end;

function TTMSFNCDataGridCore.IsBlocked: Boolean;
begin
  Result := False;
end;

function TTMSFNCDataGridCore.IsCellDisabled(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := IsCellFixed(ACell) or not IsCellVisible(ACell);
end;

function TTMSFNCDataGridCore.IsCellDisplayed(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := False;
  if IsCellFixed(ACell) then
  begin
    if IsCellFixedRoot(ACell) then
      Result := True
    else
    begin
      if IsCellFixedColumn(ACell) then
        Result := (ACell.Row >= StartRow) and (ACell.Row <= StopRow)
      else if IsCellFixedRow(ACell) then
        Result := (ACell.Column >= StartColumn) and (ACell.Column <= StopColumn)
    end;
  end
  else
    Result := (ACell.Row >= StartRow) and (ACell.Row <= StopRow) and (ACell.Column >= StartColumn) and (ACell.Column <= StopColumn);
end;

function TTMSFNCDataGridCore.GetHiddenColumnCount: Integer;
var
  k: Integer;
begin
  Result := 0;
  for k in HiddenColumnDictionary.Keys do
  begin
    if IsColumnValid(k) then
      Inc(Result);
  end;
end;

function TTMSFNCDataGridCore.GetHiddenRowCount: Integer;
var
  k: Integer;
begin
  Result := 0;
  for k in HiddenRowDictionary.Keys do
  begin
    if IsRowValid(k) and not IsRowFiltered(k) then
      Inc(Result);
  end;

  for k in FilteredRowDictionary.Keys do
  begin
    if IsRowValid(k) then
      Inc(Result);
  end;
end;

function TTMSFNCDataGridCore.GetHiddenRowHeight(ARow: Integer): Double;
begin
  if RowHeightDictionary.ContainsKey(ARow) then
    Result := RowHeightDictionary[ARow]
  else
    Result := DefaultRowHeight;
end;

procedure TTMSFNCDataGridCore.HideColumn(AColumn: Integer);
begin
  HiddenColumns[AColumn] := True;
end;

procedure TTMSFNCDataGridCore.HideRow(ARow: Integer);
begin
  HiddenRows[ARow] := True;
end;

function TTMSFNCDataGridCore.GetVisibleColumnCount: Integer;
begin
  Result := StopColumn - StartColumn + FixedRightColumnCount + FixedColumnCount + FreezeColumnCount + 1;
end;

function TTMSFNCDataGridCore.GetVisibleRowCount: Integer;
begin
  Result := StopRow - StartRow + FixedBottomRowCount + FixedRowCount + FreezeRowCount + 1;
end;

function TTMSFNCDataGridCore.GetWidth: Single;
var
  r: TRectF;
begin
  r := FLocalRect;
  Result := r.Right - r.Left;
end;

procedure TTMSFNCDataGridCore.EndUpdate;
begin
  Dec(FUpdateCount);
  if (UpdateCount = 0) then
    DoUpdate;
end;

procedure TTMSFNCDataGridCore.ExportNotification(
  AState: TTMSFNCDataGridExportState; ARow: Integer);
begin
  case AState of
    gesExportStart: BeforeExport;
    gesExportDone: AfterExport;
  end;
end;

procedure TTMSFNCDataGridCore.SetLocalRect(ARect: TRectF);
begin
  FLocalRect := ARect;
end;

procedure TTMSFNCDataGridCore.LogMessage(AMessage: string; AMessageType: TTMSFNCDataGridLogMessageType = glmInfo);
var
  m: string;
begin
  if Assigned(OnLogMessage) then
    OnLogMessage(Self, AMessage, AMessageType)
  else
  begin
    case AMessageType of
      glmInfo: m := '[INFO] ' + AMessage;
      glmWarning: m := '[WARNING] ' + AMessage;
      glmError: m := '[ERROR] ' + AMessage;
    end;

    TTMSFNCUtils.Log(m);
  end;
end;

function TTMSFNCDataGridCore.OnlyLastFullVisibleColumn: Boolean;
begin
  Result := False;
end;

function TTMSFNCDataGridCore.OnlyLastFullVisibleRow: Boolean;
begin
  Result := False;
end;

function TTMSFNCDataGridCore.FormatArray(AArray: TArray<Integer>): string;
var
  I: Integer;
  sl: TStringList;
begin
  Result := '';
  sl := TStringList.Create;
  try
    sl.LineBreak := '';
    for I := 0 to Length(AArray) - 1 do
      sl.Add(AArray[I].ToString);

    Result := sl.DelimitedText;
  finally
    sl.Free;
  end;
end;

function TTMSFNCDataGridCore.FormatNumberRange(AStartNumber,
  ACount: Integer): string;
var
  I: Integer;
  sl: TStringList;
begin
  Result := '';
  sl := TStringList.Create;
  try
    sl.LineBreak := '';
    for I := AStartNumber to AStartNumber + ACount - 1 do
      sl.Add(I.ToString);

    Result := sl.DelimitedText;
  finally
    sl.Free;
  end;
end;

function TTMSFNCDataGridCore.GetColumnWidthDictionary: TTMSFNCDataGridDoubleDictionary;
begin
  if not Assigned(FColumnWidthDictionary) then
    FColumnWidthDictionary := TTMSFNCDataGridDoubleDictionary.Create;

  Result := FColumnWidthDictionary;
end;

function TTMSFNCDataGridCore.GetColumnWidth(AColumn: Integer): Double;
begin
  if ColumnWidthDictionary.ContainsKey(AColumn) then
    Result := ColumnWidthDictionary[AColumn]
  else
    Result := DefaultColumnWidth;

  if IsColumnHidden(AColumn) then
    Result := 0;
end;

function TTMSFNCDataGridCore.GetColumnReverseOrderDictionary: TTMSFNCDataGridIntegerDictionary;
begin
  if not Assigned(FColumnReverseOrderDictionary) then
    FColumnReverseOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create;

  Result := FColumnReverseOrderDictionary;
end;

function TTMSFNCDataGridCore.GetColumnOrderDictionary: TTMSFNCDataGridIntegerDictionary;
begin
  if not Assigned(FColumnOrderDictionary) then
    FColumnOrderDictionary := TTMSFNCDataGridIntegerDictionary.Create;

  Result := FColumnOrderDictionary;
end;

function TTMSFNCDataGridCore.GetColumnReverseOrder(AColumn: Integer): Integer;
begin
  if ColumnReverseOrderDictionary.ContainsKey(AColumn) then
    Result := ColumnReverseOrderDictionary[AColumn]
  else
    Result := AColumn;
end;

function TTMSFNCDataGridCore.GetColumnOrder(AColumn: Integer): Integer;
begin
  if ColumnOrderDictionary.ContainsKey(AColumn) then
    Result := ColumnOrderDictionary[AColumn]
  else
  begin
    SetColumnOrder(AColumn, AColumn);
    Result := AColumn;
  end;
end;

function TTMSFNCDataGridCore.GetColumnPositionDictionary: TTMSFNCDataGridDoubleDictionary;
begin
  if not Assigned(FColumnPositionDictionary) then
    FColumnPositionDictionary := TTMSFNCDataGridDoubleDictionary.Create;

  Result := FColumnPositionDictionary;
end;

function TTMSFNCDataGridCore.GetColumnPosition(AColumn: Integer): Double;
var
  I: Integer;
begin
  if ColumnPositionDictionary.ContainsKey(AColumn) then
    Result := ColumnPositionDictionary[AColumn]
  else
  begin
    Result := 0;
    if AColumn > ColumnCount div 2 then
    begin
      for I := ColumnCount - 1 downto AColumn do
        Result := Result + ColumnWidths[I];

      Result := TotalColumnWidth - Result;
    end
    else
    begin
      for I := 0 to AColumn - 1 do
        Result := Result + ColumnWidths[I];
    end;

    SetColumnPosition(AColumn, Result);
  end;
end;

function TTMSFNCDataGridCore.RealToDisplayCell(
  ACell: TTMSFNCDataGridCellCoord): TTMSFNCDataGridCellCoord;
begin
  Result := MakeCell(ColumnOrders[ACell.Column], RowOrders[ACell.Row]);
end;

procedure TTMSFNCDataGridCore.RegisterRuntimeClasses;
begin

end;

procedure TTMSFNCDataGridCore.SetColumnCount(const Value: Integer);
begin
  if FColumnCount <> Value then
  begin
    FColumnCount := Value;
    SynchronizeColumns;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.DoCanLoadProperty(AObject: TObject;
  APropertyName: string; APropertyType: TTypeKind; var ACanLoad: Boolean);
begin
  if Assigned(OnCanLoadProperty) then
    OnCanLoadProperty(Self, AObject, APropertyName, APropertyType, ACanLoad);
end;

procedure TTMSFNCDataGridCore.DoCanSaveProperty(AObject: TObject;
  APropertyName: string; APropertyType: TTypeKind; var ACanSave: Boolean);
begin
  if Assigned(OnCanSaveProperty) then
    OnCanSaveProperty(Self, AObject, APropertyName, APropertyType, ACanSave);
end;

procedure TTMSFNCDataGridCore.DoUpdate;
begin
  Update;
end;

procedure TTMSFNCDataGridCore.SetColumnOrder(AColumn: Integer;
  const Value: Integer);
begin
  ColumnOrderDictionary.AddOrSetValue(AColumn, Value);
  ColumnReverseOrderDictionary.AddOrSetValue(Value, AColumn)
end;

procedure TTMSFNCDataGridCore.SetColumnWidth(AColumn: Integer; const Value: Double);
begin
  if not IsColumnValid(AColumn) then
    Exit;

  if ColumnWidthDictionary.ContainsKey(AColumn) then
  begin
    if Value = DefaultColumnWidth then
      ColumnWidthDictionary.Remove(AColumn)
    else
      ColumnWidthDictionary[AColumn] := Max(0, Value);
  end
  else if Value <> DefaultColumnWidth then
    ColumnWidthDictionary.Add(AColumn, Max(0, Value));

  DoUpdate;
end;

procedure TTMSFNCDataGridCore.SetColumnPosition(AColumn: Integer; const Value: Double);
begin
  if not IsColumnValid(AColumn) then
    Exit;

  if ColumnPositionDictionary.ContainsKey(AColumn) then
  begin
    if Value = -1 then
      ColumnPositionDictionary.Remove(AColumn)
    else
      ColumnPositionDictionary[AColumn] := Max(0, Value);
  end
  else if Value <> -1 then
    ColumnPositionDictionary.Add(AColumn, Max(0, Value));
end;

procedure TTMSFNCDataGridCore.SetDefaultColumnWidth(const Value: Double);
begin
  if FDefaultColumnWidth <> Value then
  begin
    FDefaultColumnWidth := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetDefaultRowHeight(const Value: Double);
begin
  if FDefaultRowHeight <> Value then
  begin
    FDefaultRowHeight := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetFilteredRow(ARow: Integer;
  const Value: Boolean);
begin
  if not IsRowValid(ARow) then
    Exit;

  if FilteredRowDictionary.ContainsKey(ARow) then
  begin
    if Value = False then
      FilteredRowDictionary.Remove(ARow)
    else
      FilteredRowDictionary[ARow] := Value;
  end
  else if Value <> False then
    FilteredRowDictionary.Add(ARow, Value);
end;

procedure TTMSFNCDataGridCore.SetFixedBottomRowCount(const Value: Integer);
begin
  if FFixedBottomRowCount <> Value then
  begin
    FFixedBottomRowCount := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetFixedColumnCount(const Value: Integer);
begin
  if FFixedColumnCount <> Value then
  begin
    FFixedColumnCount := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetFixedRightColumnCount(const Value: Integer);
begin
  if FFixedRightColumnCount <> Value then
  begin
    FFixedRightColumnCount := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetFixedRowCount(const Value: Integer);
begin
  if FFixedRowCount <> Value then
  begin
    FFixedRowCount := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetFreezeColumnCount(const Value: Integer);
begin
  if FFreezeColumnCount <> Value then
  begin
    FFreezeColumnCount := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetFreezeRowCount(const Value: Integer);
begin
  if FFreezeRowCount <> Value then
  begin
    FFreezeRowCount := Value;
    DoUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SetMergedColumn(AColumn: Integer;
  const Value: Boolean);
begin
  if not IsColumnValid(AColumn) then
    Exit;

  if MergedColumnDictionary.ContainsKey(AColumn) then
  begin
    if Value = False then
      MergedColumnDictionary.Remove(AColumn)
    else
      MergedColumnDictionary[AColumn] := Value;
  end
  else if Value <> False then
    MergedColumnDictionary.Add(AColumn, Value);
end;

procedure TTMSFNCDataGridCore.SetMergedRow(ARow: Integer; const Value: Boolean);
begin
  if not IsRowValid(ARow) then
    Exit;

  if MergedRowDictionary.ContainsKey(ARow) then
  begin
    if Value = False then
      MergedRowDictionary.Remove(ARow)
    else
      MergedRowDictionary[ARow] := Value;
  end
  else if Value <> False then
    MergedRowDictionary.Add(ARow, Value);
end;

procedure TTMSFNCDataGridCore.SetHeight(const Value: Single);
begin
  FLocalRect := RectF(FLocalRect.Left, FLocalRect.Top, FLocalRect.Right, FLocalRect.Top + Value);
end;

procedure TTMSFNCDataGridCore.SetHiddenColumn(AColumn: Integer;
  const Value: Boolean);
begin
  if not IsColumnValid(AColumn) then
    Exit;

  if HiddenColumnDictionary.ContainsKey(AColumn) then
  begin
    if Value = False then
      HiddenColumnDictionary.Remove(AColumn)
    else
      HiddenColumnDictionary[AColumn] := Value;
  end
  else if Value <> False then
    HiddenColumnDictionary.Add(AColumn, Value);

  DoUpdate;
end;

procedure TTMSFNCDataGridCore.SetHiddenRow(ARow: Integer; const Value: Boolean);
begin
  if not IsRowValid(ARow) then
    Exit;

  if HiddenRowDictionary.ContainsKey(ARow) then
  begin
    if Value = False then
      HiddenRowDictionary.Remove(ARow)
    else
      HiddenRowDictionary[ARow] := Value;
  end
  else if Value <> False then
    HiddenRowDictionary.Add(ARow, Value);

  DoUpdate;
end;

procedure TTMSFNCDataGridCore.SetRowHeight(ARow: Integer; const Value: Double);
begin
  if not IsRowValid(ARow) then
    Exit;

  if RowHeightDictionary.ContainsKey(ARow) then
  begin
    if Value = DefaultRowHeight then
      RowHeightDictionary.Remove(ARow)
    else
      RowHeightDictionary[ARow] := Max(0, Value);
  end
  else if Value <> DefaultRowHeight then
    RowHeightDictionary.Add(ARow, Max(0, Value));

  DoUpdate;
end;

procedure TTMSFNCDataGridCore.SetRowOrder(ARow: Integer; const Value: Integer);
begin
  RowOrderDictionary.AddOrSetValue(ARow, Value);
  RowReverseOrderDictionary.AddOrSetValue(Value, ARow);
end;

procedure TTMSFNCDataGridCore.SetRowPosition(ARow: Integer; const Value: Double);
begin
  if not IsRowValid(ARow) then
    Exit;

  if RowPositionDictionary.ContainsKey(ARow) then
  begin
    if Value = -1 then
      RowPositionDictionary.Remove(ARow)
    else
      RowPositionDictionary[ARow] := Max(0, Value);
  end
  else if Value <> -1 then
    RowPositionDictionary.Add(ARow, Max(0, Value));
end;

procedure TTMSFNCDataGridCore.SetWidth(const Value: Single);
begin
  FLocalRect := RectF(FLocalRect.Left, FLocalRect.Top, FLocalRect.Left + Value, FLocalRect.Bottom);
end;

procedure TTMSFNCDataGridCore.SetRowCount(const Value: Integer);
begin
  if FRowCount <> Value then
  begin
    FRowCount := Value;
    DoUpdate;
  end;
end;

function TTMSFNCDataGridCore.GetStartColumn: Integer;
begin
  Result := FStartColumn;
end;

function TTMSFNCDataGridCore.GetStartRow: Integer;
begin
  Result := FStartRow;
end;

function TTMSFNCDataGridCore.GetStopColumn: Integer;
begin
  Result := FStopColumn;
end;

function TTMSFNCDataGridCore.GetStopRow: Integer;
begin
  Result := FStopRow;
end;

function TTMSFNCDataGridCore.StretchColumns(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewWidth: Double = -1): Boolean;
var
  tw: Double;
  i: Integer;
  rw{$IFDEF VCLLIB}, t{$ENDIF}: Double;
  vc, stc, sec: Integer;
  r: TRectF;
begin
  Result := False;

  BeginUpdate;
  try
    if ANewWidth > -1 then
      tw := ANewWidth
    else
    begin
      r := GetContentRect;
      tw := r.Right - r.Left;
      if tw < 0 then
        Exit;
    end;

    if ARange = gstrAll then
    begin
      stc := 0;
      sec := ColumnCount - 1;
    end
    else
    begin
      stc := FixedColumnCount;
      sec := ColumnCount - 1 - FixedRightColumnCount;
    end;

    if (AMode = gstmIndex) and (AIndex = -1) then
    begin
      AIndex := sec;
      while IsColumnHidden(AIndex) and (AIndex > stc) do
        Dec(AIndex);
    end;

    vc := 0;

    for i := 0 to stc - 1 do
      tw := tw - ColumnWidths[i];

    for i := ColumnCount - 1 downto sec + 1 do
      tw := tw - ColumnWidths[i];

    for i := stc to sec do
    begin
      if not IsColumnHidden(i) then
      begin
        Inc(vc);
        if (i <> AIndex) and (AIndex <> -1) and (AMode = gstmIndex) then
          tw := tw - ColumnWidths[i];
      end;
    end;

    if AMode = gstmAll then
    begin
      if vc > 0 then
      begin
        rw := tw / vc;
        {$IFDEF VCLLIB}
        t := 0;
        {$ENDIF}
        for i := stc to sec do
        begin
          if not IsColumnHidden(i) then
          begin
            {$IFDEF VCLLIB}
            if i = sec then
              ColumnWidths[i] := Int(rw + (tw - t))
            else
              ColumnWidths[i] := Int(rw);

            t := t + Int(rw);
            {$ELSE}
            ColumnWidths[i] := rw;
            {$ENDIF}
          end;
        end;

        Result := True;
      end;
    end
    else
    begin
      if (AIndex >= 0) and (AIndex < ColumnCount) and not IsColumnHidden(AIndex) then
      begin
        rw := tw;
        if rw > 0 then
          ColumnWidths[AIndex] := rw
        else
          ColumnWidths[AIndex] := DefaultColumnWidth;

        Result := rw > 0;
      end;
    end;
  finally
    EndUpdate;
  end;
end;

function TTMSFNCDataGridCore.StretchRows(AMode: TTMSFNCDataGridStretchingMode = gstmAll; ARange: TTMSFNCDataGridStretchingRange = gstrNormal; AIndex: Integer = -1; ANewHeight: Double = -1): Boolean;
var
  th{$IFDEF VCLLIB}, t{$ENDIF}: Double;
  i: Integer;
  rh: Double;
  vr, str, ser: Integer;
  r: TRectF;
begin
  Result := False;

  BeginUpdate;
  try
    if ANewHeight > -1 then
      th := ANewHeight
    else
    begin
      r := GetContentRect;
      th := r.Bottom - r.Top;
      if th < 0 then
        Exit;
    end;

    if ARange = gstrAll then
    begin
      str := 0;
      ser := RowCount - 1;
    end
    else
    begin
      str := FixedRowCount;
      ser := RowCount - 1 - FixedBottomRowCount;
    end;

    if (AMode = gstmIndex) and (AIndex = -1) then
    begin
      AIndex := ser;
      while IsRowHidden(AIndex) and (AIndex > str) do
        Dec(AIndex);
    end;

    vr := 0;

    for i := 0 to str - 1 do
      th := th - RowHeights[i];

    for i := RowCount - 1 downto ser + 1 do
      th := th - RowHeights[i];

    for i := str to ser do
    begin
      if not IsRowHidden(i) then
      begin
        Inc(vr);
        if (i <> AIndex) and (AIndex <> -1) and (AMode = gstmIndex) then
          th := th - RowHeights[i];
      end;
    end;

    if AMode = gstmAll then
    begin
      if vr > 0 then
      begin
        rh := th / vr;
        {$IFDEF VCLLIB}
        t := 0;
        {$ENDIF}
        for i := str to ser do
        begin
          if not IsRowHidden(i) then
          begin
            {$IFDEF VCLLIB}
            if i = ser then
              RowHeights[i] := Int(rh + th - t)
            else
              RowHeights[i] := Int(rh);

            t := t + Int(rh);
            {$ELSE}
            RowHeights[i] := rh;
            {$ENDIF}
          end;
        end;

        Result := True;
      end;
    end
    else
    begin
      if (AIndex >= 0) and (AIndex < RowCount) and not IsRowHidden(AIndex) then
      begin
        rh := th;
        if rh > 0 then
          RowHeights[AIndex] := rh
        else
          RowHeights[AIndex] := DefaultRowHeight;

        Result := rh > 0;
      end;
    end;
  finally
    EndUpdate;
  end;
end;

procedure TTMSFNCDataGridCore.SynchronizeColumns(ACollectionUpdate: Boolean = False);
begin

end;

procedure TTMSFNCDataGridCore.UnhideAll;
begin
  HiddenColumnDictionary.Clear;
  HiddenRowDictionary.Clear;
  DoUpdate;
end;

procedure TTMSFNCDataGridCore.UnhideAllColumns;
begin
  HiddenColumnDictionary.Clear;
  DoUpdate;
end;

procedure TTMSFNCDataGridCore.UnhideAllRows;
begin
  HiddenRowDictionary.Clear;
  DoUpdate;
end;

procedure TTMSFNCDataGridCore.UnhideColumn(AColumn: Integer);
begin
  HiddenColumns[AColumn] := False;
end;

procedure TTMSFNCDataGridCore.UnhideRow(ARow: Integer);
begin
  HiddenRows[ARow] := False;
end;

function TTMSFNCDataGridCore.GetUpdateCount: Integer;
begin
  Result := FUpdateCount;
end;

procedure TTMSFNCDataGridCore.Update;
begin
  if not IsBlocked then
    UpdateColumnRowCalculations;
end;

procedure TTMSFNCDataGridCore.UpdateColumnRowCalculations;
var
  cr, cc: Integer;
  r, c: Double;
  k: Integer;
begin
  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  RowPositionDictionary.Clear;

  cr := RowCount - HiddenRowCount;
  r := 0;

  for k in RowHeightDictionary.Keys do
  begin
    if IsRowValid(k) and not IsRowHidden(k) then
    begin
      r := r + RowHeights[k];
      Dec(cr);
    end;
  end;

  r := r + cr * DefaultRowHeight;

  ColumnPositionDictionary.Clear;

  cc := ColumnCount - HiddenColumnCount;
  c := 0;
  for k in ColumnWidthDictionary.Keys do
  begin
    if IsColumnValid(k) and not IsColumnHidden(k) then
    begin
      c := c + ColumnWidths[k];
      Dec(cc);
    end;
  end;

  c := c + cc * DefaultColumnWidth;

  FTotalColumnWidth := c;
  FTotalRowHeight := r;
  FTotalFixedRowHeight := GetTotalFixedRowHeight(True);
  FTotalFixedColumnWidth := GetTotalFixedColumnWidth(True);
  FTotalFixedBottomRowHeight := GetTotalFixedBottomRowHeight;
  FTotalFixedRightColumnWidth := GetTotalFixedRightColumnWidth;
end;

procedure TTMSFNCDataGridCore.UpdateVisualRange;
var
  c, r: Integer;
  xval, yval, tr, br, lc, rc: Double;
  rowh, colw, cw, ch: Double;
  vpos, hpos: Double;
  cr: TRectF;
begin
  if (UpdateCount > 0) or GetIsDestroying then
    Exit;

  hpos := GetHorizontalScrollPosition;
  vpos := GetVerticalScrollPosition;
  cr := GetContentRect;
  xval := -hpos;
  yval := -vpos;
  cw := cr.Right - cr.Left;
  ch := cr.Bottom - cr.Top;

  FStartColumn := -1;
  FStartRow := -1;
  FStopColumn := -1;
  FStopRow := -1;

  lc := GetTotalFixedColumnWidth;
  rc := GetTotalFixedRightColumnWidth;

  for c := FixedColumnCount to ColumnCount - 1 - FixedRightColumnCount do
  begin
    colw := ColumnWidths[c];
    if (xval + colw > 0) and (FStartColumn = -1) then
      FStartColumn := c;

    if OnlyLastFullVisibleColumn then
    begin
      if (xval + colw + lc + ColumnWidths[c + 1] > cw - rc) and (FStopColumn = -1) then
        FStopColumn := c;
    end
    else
    begin
      if (xval + colw + lc > cw - rc) and (FStopColumn = -1) then
        FStopColumn := c;
    end;

    xval := xval + colw;

    if (FStartColumn > -1) and (FStopColumn > -1) then
      Break;
  end;

  br := GetTotalFixedBottomRowHeight;
  tr := GetTotalFixedRowHeight;

  for r := FixedRowCount to RowCount - 1 - FixedBottomRowCount do
  begin
    rowh := RowHeights[r];
    if (yval + rowh > 0) and (FStartRow = -1) then
      FStartRow := r;

    if OnlyLastFullVisibleRow then
    begin
      if (yval + rowh + tr + RowHeights[r + 1] > ch - br) and (FStopRow = -1) then
        FStopRow := r;
    end
    else
    begin
      if (yval + rowh + tr > ch - br) and (FStopRow = -1) then
        FStopRow := r;
    end;

    yval := yval + rowh;

    if (FStartRow > -1) and (FStopRow > -1) then
      Break;
  end;

  if (FStartRow > -1) and (FStopRow = -1) then
    FStopRow := RowCount - 1 - FixedBottomRowCount;

  if (FStartColumn > -1) and (FStopColumn = -1) then
    FStopColumn := ColumnCount - 1 - FixedRightColumnCount;

  FStopRow := Max(FStartRow, FStopRow);
  FStopColumn := Max(FStartColumn, FStopColumn);
end;

{ TTMSFNCDataGridCellCoord }

class function TTMSFNCDataGridCellCoord.FromString(
  AValue: string): TTMSFNCDataGridCellCoord;
var
  p: Integer;
  cs, rs: string;
  c, r: Integer;
begin
  Result := MakeCell(-1, -1);
  p := Pos(':', AValue);
  if p > 0 then
  begin
    cs := Copy(AValue, 1, p - 1);
    cs := Trim(cs);
    rs := Copy(AValue, p + 1, Length(AValue) - p);
    rs := Trim(rs);
    if TryStrToInt(cs, c) and TryStrToInt(rs, r) then
    begin
      Result := MakeCell(c, r);
    end;
  end;
end;

function TTMSFNCDataGridCellCoord.GetColumn: Integer;
begin
  Result := FColumn;
end;

function TTMSFNCDataGridCellCoord.GetRow: Integer;
begin
  Result := FRow;
end;

function TTMSFNCDataGridCellCoord.GetToString: string;
begin
  Result := IntToStr(Column) + ':' + IntToStr(Row);
end;

function TTMSFNCDataGridCellCoord.IsEqualTo(
  ACell: TTMSFNCDataGridCellCoord): Boolean;
begin
  Result := (Column = ACell.Column) and (Row = ACell.Row);
end;

procedure TTMSFNCDataGridCellCoord.SetColumn(const Value: Integer);
begin
  FColumn := Value;
end;

procedure TTMSFNCDataGridCellCoord.SetRow(const Value: Integer);
begin
  FRow := Value;
end;

end.
